import React, { useEffect } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatCurrency } from '~libs/dataUtils';

import { ErrorResponse, Spinner } from '~components';
import Icon, { faDollarSign, faUser } from '~components/icon';
import { TableView } from '~components/table-view-v3';

import columns from '~support-at-home/forms/co-contributions/clientEstimateList';

import { useEstimateQuery } from '~support-at-home/actions/clientInvoices';

import { SummaryStatCard } from '../../claims/common';

interface ComponentProps {
  value: { invoice_date: string; service_date_to?: string };
  onChange?: (name: string, value: any) => void;
}

const Confirm: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { invoice_date, service_date_to } = value;
  const { data, error, isLoading, isError } = useEstimateQuery({
    invoice_date,
    ...(service_date_to ? { service_date_to } : {}),
  });

  // Expose data to parent form for validation
  useEffect(() => {
    if (onChange && data) {
      onChange('confirm', { data });
    }
  }, [data]);

  const clientCount = data?.clients?.length ?? 0;

  return (
    <div>
      <div className="d-flex flex-wrap gap-4">
        <div className="flex-fill">
          {isLoading ? (
            <Spinner loading={isLoading} />
          ) : (
            <SummaryStatCard
              count={<span>{formatCurrency(data?.overall_total)}</span>}
              title="Total Amount"
              icon={<Icon icon={faDollarSign} />}
              countClassName="text-secondary"
            />
          )}
        </div>
        <div className="flex-fill">
          {isLoading ? (
            <Spinner loading={isLoading} />
          ) : (
            <SummaryStatCard
              count={<span>{clientCount}</span>}
              title="Clients"
              icon={<Icon icon={faUser} />}
              countClassName="text-primary"
            />
          )}
        </div>
      </div>

      {isError && <ErrorResponse message={getErrorMessage(error)} />}

      {!isError && !isLoading && clientCount === 0 && (
        <ErrorResponse message="No uninvoiced co-contributions found for selected dates" />
      )}

      {!isError && (isLoading || clientCount > 0) && (
        <div>
          <div className="mt-4">
            <TableView
              id="co-contributions-table"
              columns={columns}
              data={data?.clients || []}
              isLoading={isLoading}
              emptyMessage="No co-contributions found"
              readonly
              striped
              bordered
            />
          </div>
          <div className="row fw-bold p-2 text-end border-start border-end border-bottom">
            <div className="col">
              <span>
                Total: <span className="ms-4">{formatCurrency(data?.overall_total)}</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Confirm;
